import { Box } from "@mui/material";
import moment from "moment-timezone";
import { useEffect, useMemo, useState } from "react";
import { getMarketClose } from "../sevice/home";


const DUBAI = "Asia/Dubai";


function normalizeCloseType(type) {
  if (typeof type !== "string") return "";
  return type.trim().toLowerCase();
}




function isSameCalendarDayForClose(close, dayMoment) {
  const t = normalizeCloseType(close.type);
  if (t === "once") {
    const closeDate = moment.tz(String(close.date).trim(), "DD/MM/YYYY", DUBAI);
    if (!closeDate.isValid()) return false;
    return (
      closeDate.year() === dayMoment.year() &&
      closeDate.month() === dayMoment.month() &&
      closeDate.date() === dayMoment.date()
    );
  }
  if (t === "repeat") {
    const parts = String(close.date).split("/").map(Number);
    if (parts.length < 2) return false;
    const [day, month] = parts;
    return dayMoment.month() + 1 === month && dayMoment.date() === day;
  }
  return false;
}




function isMetalsClosed(dubaiTime) {
  const dayOfWeek = dubaiTime.isoWeekday();
  const hour = dubaiTime.hour();


  if (dayOfWeek === 6 && hour <= 1) {
    return false;
  }


  if (dayOfWeek === 6 || dayOfWeek === 7) {
    return true;
  }


  if (hour === 2) {
    return true;
  }
  if (dayOfWeek === 1 && hour <= 2) {
    return true;
  }


  return false;
}




function isSpecialDayMarketCloseAt(marketClose, dubaiTime) {
  if (!Array.isArray(marketClose) || marketClose.length === 0) {
    return false;
  }


  const today = dubaiTime.clone();
  const yesterday = dubaiTime.clone().subtract(1, "day");


  const isTodayMarketClose = marketClose.some((close) =>
    isSameCalendarDayForClose(close, today)
  );


  const wasYesterdayMarketClose = marketClose.some((close) =>
    isSameCalendarDayForClose(close, yesterday)
  );


  if (isTodayMarketClose) {
    if (today.hour() < 2) {
      return false;
    }
    return true;
  }


  if (wasYesterdayMarketClose) {
    const marketOpenTime = today
      .clone()
      .hour(3)
      .minute(0)
      .second(0)
      .millisecond(0);
    if (today.isBefore(marketOpenTime)) {
      return true;
    }
  }


  return false;
}




function getSpecialCloseReason(marketClose, dubaiTime) {
  if (!Array.isArray(marketClose) || marketClose.length === 0) return null;


  const today = dubaiTime.clone();
  const yesterday = dubaiTime.clone().subtract(1, "day");


  const reasonForDay = (dayMoment) => {
    for (const close of marketClose) {
      if (!isSameCalendarDayForClose(close, dayMoment)) continue;
      if (typeof close.reason === "string" && close.reason.trim()) {
        return close.reason.trim();
      }
    }
    return null;
  };


  const isTodayMarketClose = marketClose.some((close) =>
    isSameCalendarDayForClose(close, today)
  );
  if (isTodayMarketClose) {
    if (today.hour() < 2) return null;
    return reasonForDay(today);
  }


  const wasYesterdayMarketClose = marketClose.some((close) =>
    isSameCalendarDayForClose(close, yesterday)
  );
  if (wasYesterdayMarketClose) {
    const marketOpenTime = today
      .clone()
      .hour(3)
      .minute(0)
      .second(0)
      .millisecond(0);
    if (today.isBefore(marketOpenTime)) {
      return reasonForDay(yesterday);
    }
  }


  return null;
}


function normalizeMarketClosePayload(res) {
  if (Array.isArray(res)) return res;
  if (res?.marketClose && Array.isArray(res.marketClose)) return res.marketClose;
  if (res?.data && Array.isArray(res.data)) return res.data;
  return [];
}


/**
 * Schedule first, then special days (short-circuit).
 */
function isMarketClosed(marketCloseList, dubaiTime) {
  if (isMetalsClosed(dubaiTime)) return true;
  if (isSpecialDayMarketCloseAt(marketCloseList, dubaiTime)) return true;
  return false;
}


export default function MarketClose() {
  const [marketCloseList, setMarketCloseList] = useState([]);
  const [specialReason, setSpecialReason] = useState(null);
  const [isClosed, setIsClosed] = useState(false);
  const [timeDifference, setTimeDifference] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });


  useEffect(() => {
    const fetchMarketCloseTime = async () => {
      const res = await getMarketClose();
      setMarketCloseList(normalizeMarketClosePayload(res));
    };
    fetchMarketCloseTime();
  }, []);


  useEffect(() => {
    const tick = () => {
      const dubai = moment().tz(DUBAI);
      const closed = isMarketClosed(marketCloseList, dubai);
      setIsClosed(closed);
      setSpecialReason(
        closed ? getSpecialCloseReason(marketCloseList, dubai) : null
      );


      if (!closed) {
        setTimeDifference({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }


      let nextOpen = null;
      const probe = dubai.clone().add(1, "minute").startOf("minute");
      if (!probe.isAfter(dubai)) {
        probe.add(1, "minute");
      }
      const maxProbe = dubai.clone().add(7, "days");
      for (let i = 0; i < 7 * 24 * 60 && probe.isBefore(maxProbe); i++) {
        if (!isMarketClosed(marketCloseList, probe)) {
          nextOpen = probe.clone();
          break;
        }
        probe.add(1, "minute");
      }


      if (nextOpen) {
        const ms = nextOpen.diff(dubai);
        const dur = moment.duration(ms);
        setTimeDifference({
          days: Math.floor(dur.asDays()),
          hours: dur.hours(),
          minutes: dur.minutes(),
          seconds: dur.seconds(),
        });
      } else {
        setTimeDifference({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };


    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [marketCloseList]);


  const formattedTimeDifference = useMemo(() => {
    const { days, hours, minutes, seconds } = timeDifference;
    const parts = [];
    if (days > 0) parts.push(`${days} day${days === 1 ? "" : "s"}`);
    if (hours > 0) parts.push(`${hours} hour${hours === 1 ? "" : "s"}`);
    if (minutes > 0) parts.push(`${minutes} minute${minutes === 1 ? "" : "s"}`);
    parts.push(`${seconds} second${seconds === 1 ? "" : "s"}`);
    return parts.join(" ");
  }, [timeDifference]);


  if (!isClosed) {
    return null;
  }


  return (
    <Box
      sx={{
        position: "fixed",
        top: "0",
        left: "-125px",
        right: "0",
        bottom: "0",
        width: "895px",
        height: "531px",
        overflow: "hidden",
        zIndex: 10000,
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "10%",
          transform: "rotate(-35deg)",
          display: "flex",
          alignItems: "center",
          backgroundColor: "#FFFFFF",
          color: "#362F22",
          fontSize: "2vw",
          fontWeight: "bold",
          padding: "1vw",
        }}
      >
        <marquee>
          <p style={{ marginLeft: "90px" }}>
            {specialReason
              ? `${specialReason} `
              : "Market closed! "}
            {formattedTimeDifference
              ? `Opens in ${formattedTimeDifference}`
              : ""}
          </p>
        </marquee>
      </div>
    </Box>
  );
}
